import { useEffect, useRef, useState } from "react"

// Вышла ли новая версия фронта (KRE-773): пока вкладка открыта или когда
// браузер открыл сайт из кэша.
//
// Вкладку держат открытой сутками, а после выката в ней остаётся старый код.
// Service worker, как в Axios CRM, ради этого не заводим: у каждой сборки своё
// имя бандла (main.<хэш>.js), и оно прописано в index.html. Спрашиваем свежий
// index.html и сравниваем имя бандла в нём с тем, на котором работает вкладка.

// Как часто проверять, пока вкладка открыта и на виду
const POLL_INTERVAL_MS = 30 * 60 * 1000
// Не чаще раза в минуту: переключение окон не должно плодить запросы
const MIN_CHECK_GAP_MS = 60 * 1000

const BUNDLE_PATTERN = /\/static\/js\/main\.[0-9a-f]+\.js/

export const findBundleName = (html: string): string | undefined =>
  html.match(BUNDLE_PATTERN)?.[0]

// Бандл, на котором работает вкладка. В dev (yarn start) бандл без хэша
// (bundle.js) — тогда undefined, и проверка не запускается
const getCurrentBundleName = (): string | undefined =>
  findBundleName(Array.from(document.scripts, (script) => script.getAttribute("src") ?? "").join(" "))

export const useIsNewVersionAvailable = (): boolean => {
  const [isAvailable, setIsAvailable] = useState(false)
  const lastCheckRef = useRef(0)

  useEffect(() => {
    const currentBundle = getCurrentBundleName()
    // Когда новая версия уже нашлась, больше не спрашиваем: пока страницу не
    // перезагрузили, код в ней всё равно старый
    if (!currentBundle || isAvailable) {
      return
    }

    const check = async () => {
      if (!navigator.onLine || document.visibilityState !== "visible") {
        return
      }
      const now = Date.now()
      if (now - lastCheckRef.current < MIN_CHECK_GAP_MS) {
        return
      }
      lastCheckRef.current = now

      try {
        // Параметр — чтобы не получить index.html из кэша по дороге
        const response = await fetch(`/index.html?t=${now}`, { cache: "no-store" })
        // Во время выката сервер может отвечать 502 — это ещё не новая версия
        if (!response.ok) {
          return
        }
        const latestBundle = findBundleName(await response.text())
        if (latestBundle && latestBundle !== currentBundle) {
          setIsAvailable(true)
        }
      } catch {
        // Нет сети или сервер перезапускается — проверим в следующий раз
      }
    }

    // Сразу при открытии: index.html отдаётся без Cache-Control, и браузер
    // может открыть сайт из кэша на старой версии — тогда плашка видна сразу,
    // как в Axios CRM, а не после возврата к вкладке
    check()

    // Кроме таймера — при возврате к вкладке и при появлении сети: выкаты
    // вечером, а вкладка с утра стоит в фоне или ноутбук спал
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        check()
      }
    }
    const timer = window.setInterval(check, POLL_INTERVAL_MS)
    document.addEventListener("visibilitychange", onVisibilityChange)
    window.addEventListener("focus", check)
    window.addEventListener("online", check)

    return () => {
      window.clearInterval(timer)
      document.removeEventListener("visibilitychange", onVisibilityChange)
      window.removeEventListener("focus", check)
      window.removeEventListener("online", check)
    }
  }, [isAvailable])

  return isAvailable
}
